import {
  Image as ImageIcon,
  FileText,
  FileCode,
  FileArchive,
  FileAudio,
  FileVideo,
  Key,
  File as FileIcon,
  type LucideIcon,
} from 'lucide-react';

export type AttachmentCategory =
  | 'image'
  | 'text'
  | 'code'
  | 'archive'
  | 'audio'
  | 'video'
  | 'key'
  | 'other';

export const CATEGORY_ORDER: Record<AttachmentCategory, number> = {
  image: 0,
  text: 1,
  code: 2,
  key: 3,
  archive: 4,
  audio: 5,
  video: 6,
  other: 7,
};

const TEXT_PREFIXES = ['text/'];
const TEXT_EXACT = [
  'application/json',
  'application/xml',
  'application/x-yaml',
  'application/yaml',
  'application/x-sh',
  'application/x-pem-file',
  'application/pkcs8',
];
const CODE_EXACT = [
  'application/javascript',
  'application/typescript',
  'application/x-sh',
];
const KEY_HINTS = [
  'application/x-pem-file',
  'application/pkcs8',
  'application/x-x509',
];
const ARCHIVE_EXACT = [
  'application/zip',
  'application/x-tar',
  'application/x-gzip',
  'application/x-7z-compressed',
  'application/x-rar-compressed',
];

const KEY_FILENAME_PATTERNS = [
  /^id_rsa($|\.)/i,
  /^id_ed25519($|\.)/i,
  /^id_ecdsa($|\.)/i,
  /^id_dsa($|\.)/i,
  /\.pem$/i,
  /\.key$/i,
  /\.pub$/i,
  /\.ppk$/i,
];

export function categoryFor(mimetype: string, filename = ''): AttachmentCategory {
  const m = (mimetype || '').toLowerCase();

  if (KEY_FILENAME_PATTERNS.some((r) => r.test(filename))) return 'key';
  if (KEY_HINTS.includes(m)) return 'key';

  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('audio/')) return 'audio';
  if (m.startsWith('video/')) return 'video';
  if (CODE_EXACT.includes(m)) return 'code';
  if (TEXT_PREFIXES.some((p) => m.startsWith(p))) return 'text';
  if (TEXT_EXACT.includes(m)) return 'text';
  if (ARCHIVE_EXACT.includes(m)) return 'archive';

  // Extension fallback when mimetype is empty / generic
  const ext = filename.toLowerCase().split('.').pop() ?? '';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'ico'].includes(ext)) return 'image';
  if (['txt', 'md', 'log', 'csv', 'json', 'yaml', 'yml', 'xml', 'ini', 'conf'].includes(ext))
    return 'text';
  if (['js', 'ts', 'tsx', 'jsx', 'py', 'rb', 'go', 'rs', 'java', 'c', 'cpp', 'h', 'sh'].includes(ext))
    return 'code';
  if (['zip', 'tar', 'gz', '7z', 'rar'].includes(ext)) return 'archive';
  if (['mp3', 'wav', 'flac', 'ogg', 'm4a'].includes(ext)) return 'audio';
  if (['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(ext)) return 'video';

  return 'other';
}

export function iconFor(category: AttachmentCategory): LucideIcon {
  switch (category) {
    case 'image':
      return ImageIcon;
    case 'text':
      return FileText;
    case 'code':
      return FileCode;
    case 'key':
      return Key;
    case 'archive':
      return FileArchive;
    case 'audio':
      return FileAudio;
    case 'video':
      return FileVideo;
    default:
      return FileIcon;
  }
}

export function isTextPreviewable(category: AttachmentCategory): boolean {
  return category === 'text' || category === 'code' || category === 'key';
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** MIME type used on HTML5 dataTransfer when dragging attachments. */
export const ATTACHMENT_DRAG_MIME = 'application/x-dragonvault-attachment';

export interface AttachmentDragPayload {
  id: string;
  filename: string;
  mimetype: string;
  category: AttachmentCategory;
}

/** Reference text written into a target document's markdown when an attachment is dropped. */
export function buildAttachmentRef(p: AttachmentDragPayload): string {
  const prefix = p.category === 'image' ? '📷' : '📎';
  const safeName = p.filename.replace(/[\[\]()]/g, '');
  return `[${prefix} ${safeName}](attachment:${p.id})`;
}

/** Custom DOM event fired after a successful attachment drop, so EditorPane can refresh. */
export const DOC_UPDATED_EVENT = 'dragonvault:doc-updated';

/** Read an attachment payload from a drag event, if present. */
export function readAttachmentDrag(e: React.DragEvent | DragEvent): AttachmentDragPayload | null {
  const raw = e.dataTransfer?.getData(ATTACHMENT_DRAG_MIME);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AttachmentDragPayload;
  } catch {
    return null;
  }
}
